import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, MessageCircle, FileText, Plus, Trash2, Save, ExternalLink, Edit2, X, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminClubVIP = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  
  // Formulario de edición
  const [telegramLink, setTelegramLink] = useState('');
  const [whatsappLink, setWhatsappLink] = useState('');
  const [extraMaterials, setExtraMaterials] = useState([]);
  
  // Modal para agregar material
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    title: '',
    description: '',
    url: ''
  });

  useEffect(() => {
    checkAuth();
    fetchPrograms();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.role !== 'admin') {
        navigate('/dashboard');
      }
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/programs`);
      setPrograms(res.data || []);
      
      // Seleccionar el programa Premium por defecto
      const premiumProgram = res.data.find(p => 
        p.level === 'premium' || 
        p.name?.toLowerCase().includes('premium') ||
        p.id === 'premium'
      );
      
      if (premiumProgram) {
        selectProgram(premiumProgram);
      }
    } catch (error) {
      toast.error('Error al cargar programas');
    } finally {
      setLoading(false);
    }
  };

  const selectProgram = (program) => {
    setSelectedProgram(program);
    setTelegramLink(program.telegram_link || '');
    setWhatsappLink(program.whatsapp_link || '');
    setExtraMaterials(program.extra_materials || []);
  };

  const handleSave = async () => {
    if (!selectedProgram) return;
    
    setSaving(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/programs/${selectedProgram._id || selectedProgram.program_id}`,
        {
          ...selectedProgram,
          telegram_link: telegramLink,
          whatsapp_link: whatsappLink,
          extra_materials: extraMaterials
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('¡Configuración guardada!');
      
      // Actualizar el programa en la lista local
      setPrograms(prev => prev.map(p => 
        (p._id || p.program_id) === (selectedProgram._id || selectedProgram.program_id)
          ? { ...p, telegram_link: telegramLink, whatsapp_link: whatsappLink, extra_materials: extraMaterials }
          : p
      ));
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const openMaterialModal = (material = null, index = null) => {
    if (material) {
      setEditingMaterial(index);
      setMaterialForm({
        title: material.title,
        description: material.description || '',
        url: material.url
      });
    } else {
      setEditingMaterial(null);
      setMaterialForm({ title: '', description: '', url: '' });
    }
    setShowMaterialModal(true);
  };

  const saveMaterial = () => {
    if (!materialForm.title || !materialForm.url) {
      toast.error('Título y URL son obligatorios');
      return;
    }

    if (editingMaterial !== null) {
      // Editando material existente
      setExtraMaterials(prev => prev.map((m, i) => 
        i === editingMaterial ? materialForm : m
      ));
    } else {
      // Agregando nuevo material
      setExtraMaterials(prev => [...prev, materialForm]);
    }
    
    setShowMaterialModal(false);
    setMaterialForm({ title: '', description: '', url: '' });
    toast.success(editingMaterial !== null ? 'Material actualizado' : 'Material agregado');
  };

  const deleteMaterial = (index) => {
    setExtraMaterials(prev => prev.filter((_, i) => i !== index));
    toast.success('Material eliminado');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/admin')}
            className="inline-flex items-center text-zinc-400 hover:text-pink-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Panel Admin
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Club VIP</h1>
              <p className="text-zinc-400">Gestiona comunidad y material exclusivo para programas</p>
            </div>
          </div>
        </div>

        {/* Selector de programa */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 mb-6">
          <h2 className="text-lg font-bold text-white mb-4">Seleccionar Programa</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {programs.map(program => (
              <button
                key={program._id || program.program_id}
                onClick={() => selectProgram(program)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  (selectedProgram?._id || selectedProgram?.program_id) === (program._id || program.program_id)
                    ? 'border-pink-500 bg-pink-500/20'
                    : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                }`}
              >
                <span className={`font-medium ${
                  (selectedProgram?._id || selectedProgram?.program_id) === (program._id || program.program_id)
                    ? 'text-white'
                    : 'text-zinc-300'
                }`}>
                  {program.name}
                </span>
                {program.level === 'premium' && (
                  <span className="ml-2 text-yellow-500">👑</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {selectedProgram && (
          <>
            {/* Comunidad */}
            <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 mb-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <MessageCircle className="w-6 h-6 text-blue-400" />
                Comunidad
              </h2>
              
              <div className="space-y-4">
                {/* Telegram */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Enlace de Telegram
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={telegramLink}
                      onChange={(e) => setTelegramLink(e.target.value)}
                      placeholder="https://t.me/tu_grupo_privado"
                      className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                    />
                    {telegramLink && (
                      <a
                        href={telegramLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 text-white" />
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    Los usuarios del programa verán un botón para unirse a este grupo
                  </p>
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Enlace de WhatsApp (opcional)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={whatsappLink}
                      onChange={(e) => setWhatsappLink(e.target.value)}
                      placeholder="https://chat.whatsapp.com/..."
                      className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                    />
                    {whatsappLink && (
                      <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 text-white" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Material Extra */}
            <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <FileText className="w-6 h-6 text-green-400" />
                  Material Extra
                </h2>
                <Button
                  onClick={() => openMaterialModal()}
                  className="bg-green-600 hover:bg-green-500 text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Material
                </Button>
              </div>
              
              <p className="text-zinc-400 text-sm mb-4">
                Agrega enlaces de Google Drive con PDFs, guías, material de entrenamiento, etc.
              </p>

              {extraMaterials.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay material agregado</p>
                  <p className="text-sm">Haz clic en "Agregar Material" para comenzar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {extraMaterials.map((material, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-xl border border-zinc-700"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <LinkIcon className="w-4 h-4 text-green-400" />
                          <span className="font-medium text-white">{material.title}</span>
                        </div>
                        {material.description && (
                          <p className="text-sm text-zinc-400 mt-1">{material.description}</p>
                        )}
                        <a 
                          href={material.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300 mt-1 block truncate max-w-md"
                        >
                          {material.url}
                        </a>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => openMaterialModal(material, index)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteMaterial(index)}
                          className="p-2 text-red-500 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botón Guardar */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 font-bold text-lg"
            >
              {saving ? (
                'Guardando...'
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Guardar Configuración de {selectedProgram.name}
                </>
              )}
            </Button>
          </>
        )}

        {/* Modal para agregar/editar material */}
        {showMaterialModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 rounded-2xl p-6 w-full max-w-md border border-zinc-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">
                  {editingMaterial !== null ? 'Editar Material' : 'Agregar Material'}
                </h3>
                <button
                  onClick={() => setShowMaterialModal(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Título <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={materialForm.title}
                    onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                    placeholder="Ej: Guía de Suplementación"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Descripción (opcional)
                  </label>
                  <Input
                    value={materialForm.description}
                    onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                    placeholder="Ej: PDF con recomendaciones de suplementos"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    URL de Google Drive <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={materialForm.url}
                    onChange={(e) => setMaterialForm({ ...materialForm, url: e.target.value })}
                    placeholder="https://drive.google.com/..."
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Asegúrate de que el enlace tenga permisos de visualización
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowMaterialModal(false)}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-zinc-300"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={saveMaterial}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                >
                  {editingMaterial !== null ? 'Actualizar' : 'Agregar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminClubVIP;
